import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../styles/smrttasks.css";
import SMRTTasksAddTypeModal from "../components/SMRTTasksAddTypeModal";

type TopTab = "overview" | "open" | "rejected" | "complete";
type TaskTab = "open" | "rejected" | "complete";
type SortKey =
  | "title"
  | "taskType"
  | "assignedBy"
  | "assignedTo"
  | "completed"
  | "dateStarted"
  | "dateCompleted";
type SortDir = "asc" | "desc";
type SortState = { key: SortKey | null; dir: SortDir };

interface TaskTypeWithCounts {
  id: string;
  name: string;
  displayOrder: number;
  createdAt?: string;
  counts: {
    open: number;
    rejected: number;
    complete: number;
  };
}

interface AdminTask {
  id: string;
  orgID: string;
  title: string;
  description?: string;
  taskTypeId: string;
  assignedUserIds: string[];
  createdBy: string;
  createdAt: string;
  dueDate?: string;
  status: "Open" | "Complete" | "Rejected";
  isPrivate: boolean;
  completedAt?: string;
  completedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectedReason?: string;
  source?: "crmtix";
}

interface OrgUser {
  user_id: string;
  name?: string;
  email: string;
}

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/smrt-task-types`;
const ADMIN_TASKS_API = `${process.env.REACT_APP_BACKEND_URL}/smrt-tasks/admin`;
const USERS_API = `${process.env.REACT_APP_BACKEND_URL}/approved_users`;

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ManageSMRTTasks() {
  const [activeTab, setActiveTab] = useState<TopTab>("overview");
  const [taskTypes, setTaskTypes] = useState<TaskTypeWithCounts[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingInitialName, setEditingInitialName] = useState<string>("");

  const [adminTasks, setAdminTasks] = useState<{
    open: AdminTask[];
    rejected: AdminTask[];
    complete: AdminTask[];
  }>({ open: [], rejected: [], complete: [] });
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [tasksLoading, setTasksLoading] = useState<boolean>(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortState, setSortState] = useState<Record<TaskTab, SortState>>({
    open: { key: null, dir: "asc" },
    rejected: { key: null, dir: "asc" },
    complete: { key: null, dir: "asc" },
  });

  const taskTypeName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of taskTypes) map[t.id] = t.name;
    return (id: string) => map[id] || "Unknown";
  }, [taskTypes]);

  const userName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users) map[u.user_id] = u.name || u.email;
    return (id: string) => map[id] || id;
  }, [users]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const loadAdminTasks = useCallback(async () => {
    setTasksLoading(true);
    setTasksError(null);
    try {
      const [tasksRes, usersRes] = await Promise.all([
        fetch(ADMIN_TASKS_API, { credentials: "include" }),
        fetch(USERS_API, { credentials: "include" }),
      ]);
      if (!tasksRes.ok)
        throw new Error(`Failed to load tasks (${tasksRes.status})`);
      const td = await tasksRes.json();
      setAdminTasks({
        open: Array.isArray(td?.open) ? td.open : [],
        rejected: Array.isArray(td?.rejected) ? td.rejected : [],
        complete: Array.isArray(td?.complete) ? td.complete : [],
      });
      if (usersRes.ok) {
        const ud = await usersRes.json();
        setUsers(Array.isArray(ud) ? ud : []);
      }
    } catch (err: unknown) {
      setTasksError((err as Error).message || "Failed to load tasks");
    } finally {
      setTasksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdminTasks();
  }, [loadAdminTasks]);

  const loadTaskTypes = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(API_BASE, { credentials: "include" });
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const data = await res.json();
      const list: TaskTypeWithCounts[] = Array.isArray(data?.taskTypes)
        ? data.taskTypes
        : [];
      list.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      setTaskTypes(list);
    } catch (err: unknown) {
      setLoadError((err as Error).message || "Failed to load task types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTaskTypes();
  }, [loadTaskTypes]);

  const openCreate = () => {
    setModalMode("create");
    setEditingId(null);
    setEditingInitialName("");
    setModalOpen(true);
  };

  const openEdit = (tt: TaskTypeWithCounts) => {
    setModalMode("edit");
    setEditingId(tt.id);
    setEditingInitialName(tt.name);
    setModalOpen(true);
  };

  const handleModalSubmit = async (name: string) => {
    if (modalMode === "create") {
      const res = await fetch(API_BASE, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed to create (${res.status})`);
      }
    } else if (modalMode === "edit" && editingId) {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(editingId)}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed to update (${res.status})`);
      }
    }
    setModalOpen(false);
    await loadTaskTypes();
  };

  const handleDelete = async (tt: TaskTypeWithCounts) => {
    const confirmed = window.confirm(
      `Delete task type "${tt.name}"? This cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(tt.id)}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed to delete (${res.status})`);
      }
      await loadTaskTypes();
    } catch (err: unknown) {
      window.alert((err as Error).message || "Failed to delete task type");
    }
  };

  const persistOrder = async (orderedIds: string[]) => {
    try {
      const res = await fetch(`${API_BASE}/reorder`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderedIds }),
      });
      if (!res.ok) throw new Error(`Failed to reorder (${res.status})`);
    } catch (err: unknown) {
      window.alert((err as Error).message || "Failed to save new order");
      await loadTaskTypes();
    }
  };

  const swap = (fromIndex: number, toIndex: number) => {
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= taskTypes.length ||
      toIndex >= taskTypes.length
    ) {
      return;
    }
    const next = taskTypes.slice();
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    const renumbered = next.map((t, i) => ({ ...t, displayOrder: i }));
    setTaskTypes(renumbered);
    persistOrder(renumbered.map((t) => t.id));
  };

  const handleMoveUp = (id: string) => {
    const idx = taskTypes.findIndex((t) => t.id === id);
    if (idx > 0) swap(idx, idx - 1);
  };

  const handleMoveDown = (id: string) => {
    const idx = taskTypes.findIndex((t) => t.id === id);
    if (idx >= 0 && idx < taskTypes.length - 1) swap(idx, idx + 1);
  };

  const handleSort = (tab: TaskTab, key: SortKey) => {
    setSortState((prev) => {
      const cur = prev[tab];
      const next: SortState =
        cur.key === key
          ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
          : { key, dir: "asc" };
      return { ...prev, [tab]: next };
    });
  };

  const sortIndicator = (tab: TaskTab, key: SortKey) => {
    const s = sortState[tab];
    if (s.key !== key) return "";
    return s.dir === "asc" ? " ↑" : " ↓";
  };

  const sortedTasks = (tab: TaskTab, tasks: AdminTask[]): AdminTask[] => {
    const s = sortState[tab];
    if (!s.key) return tasks;
    const dir = s.dir === "asc" ? 1 : -1;
    const get = (t: AdminTask): string => {
      switch (s.key) {
        case "title":
          return (t.title || "").toLowerCase();
        case "taskType":
          return taskTypeName(t.taskTypeId).toLowerCase();
        case "assignedBy":
          return (t.source === "crmtix"
            ? "CRMTIX Cheques"
            : userName(t.createdBy)
          ).toLowerCase();
        case "assignedTo":
          return (t.assignedUserIds || [])
            .map((id) => userName(id))
            .join(",")
            .toLowerCase();
        case "completed":
          return userName(
            t.completedBy || t.rejectedBy || "",
          ).toLowerCase();
        case "dateStarted":
          return t.createdAt || "";
        case "dateCompleted":
          return t.completedAt || t.rejectedAt || "";
        default:
          return "";
      }
    };
    return [...tasks].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  };

  const removeTasksFromState = (ids: Set<string>) => {
    if (ids.size === 0) return;
    setAdminTasks((prev) => ({
      open: prev.open.filter((t) => !ids.has(t.id)),
      rejected: prev.rejected.filter((t) => !ids.has(t.id)),
      complete: prev.complete.filter((t) => !ids.has(t.id)),
    }));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  };

  const deleteAdminTaskRequest = async (task: AdminTask): Promise<void> => {
    const url = `${ADMIN_TASKS_API}/${encodeURIComponent(task.id)}?orgID=${encodeURIComponent(task.orgID)}`;
    const res = await fetch(url, { method: "DELETE", credentials: "include" });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.error || `Failed to delete (${res.status})`);
    }
  };

  const handleAdminDelete = async (task: AdminTask) => {
    try {
      await deleteAdminTaskRequest(task);
      removeTasksFromState(new Set([task.id]));
    } catch (err: unknown) {
      window.alert((err as Error).message || "Failed to delete task");
    }
  };

  const handleBulkDelete = async () => {
    const targets = adminTasks.complete.filter((t) => selectedIds.has(t.id));
    if (targets.length === 0) return;
    const deleted = new Set<string>();
    const errors: string[] = [];
    for (const task of targets) {
      try {
        await deleteAdminTaskRequest(task);
        deleted.add(task.id);
      } catch (err: unknown) {
        errors.push(`${task.title}: ${(err as Error).message || "delete failed"}`);
      }
    }
    removeTasksFromState(deleted);
    if (errors.length > 0) {
      window.alert(`Some deletes failed:\n${errors.join("\n")}`);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderAdminTaskRow = (task: AdminTask, tab: TaskTab) => {
    const isRejected = task.status === "Rejected";
    const isExpanded = expandedIds.has(task.id);
    const titleColorClass = isRejected
      ? " smrttasks-task-title-rejected"
      : "";
    const typeLabel = taskTypeName(task.taskTypeId);
    const assigneesText =
      task.assignedUserIds.length === 0
        ? "—"
        : task.assignedUserIds.map((id) => userName(id)).join(", ");
    const closerID = task.completedBy || task.rejectedBy;
    const closerLabel = closerID ? userName(closerID) : "—";
    const createdByLabel =
      task.source === "crmtix" ? "CRMTIX Cheques" : userName(task.createdBy);
    const dateStarted = formatDate(task.createdAt);
    const closedAt = task.completedAt || task.rejectedAt;
    const dateCompleted = closedAt ? formatDate(closedAt) : "—";

    return (
      <div
        key={task.id}
        className="smrttasks-task-row smrttasks-task-row-admin"
      >
        <div className="smrttasks-task-main">
          <div className={`smrttasks-task-title${titleColorClass}`}>
            {tab === "complete" && (
              <input
                type="checkbox"
                className="smrttasks-row-check"
                checked={selectedIds.has(task.id)}
                onChange={() => toggleSelect(task.id)}
                title="Select for bulk delete"
              />
            )}
            {task.title}
          </div>
          {isExpanded && task.description && (
            <div className="smrttasks-task-description">{task.description}</div>
          )}
          {isExpanded && isRejected && task.rejectedReason && (
            <div className="smrttasks-task-rejection">
              <strong>Reason:</strong> {task.rejectedReason}
            </div>
          )}
        </div>

        <div className="smrttasks-task-cell" title={typeLabel}>
          {typeLabel}
        </div>
        <div className="smrttasks-task-cell" title={createdByLabel}>
          {createdByLabel}
        </div>
        <div className="smrttasks-task-cell" title={assigneesText}>
          {assigneesText}
        </div>
        <div className="smrttasks-task-cell" title={closerLabel}>
          {closerLabel}
        </div>
        <div className="smrttasks-task-date">{dateStarted}</div>
        <div className="smrttasks-task-date">{dateCompleted}</div>

        <div className="smrttasks-task-actions">
          <button
            type="button"
            className="smrttasks-btn-icon smrttasks-btn-view"
            title={isExpanded ? "Hide description" : "Show description"}
            onClick={() => toggleExpand(task.id)}
          >
            👁
          </button>
          {tab === "complete" && (
            <button
              type="button"
              className="smrttasks-btn-icon smrttasks-btn-delete"
              title="Delete task"
              onClick={() => handleAdminDelete(task)}
            >
              🗑
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderAdminTaskHeader = (tab: TaskTab) => (
    <div className="smrttasks-task-header smrttasks-task-header-admin">
      <div
        className="smrttasks-sortable"
        onClick={() => handleSort(tab, "title")}
      >
        Task{sortIndicator(tab, "title")}
      </div>
      <div
        className="smrttasks-sortable"
        onClick={() => handleSort(tab, "taskType")}
      >
        Task Type{sortIndicator(tab, "taskType")}
      </div>
      <div
        className="smrttasks-sortable"
        onClick={() => handleSort(tab, "assignedBy")}
      >
        Assigned By{sortIndicator(tab, "assignedBy")}
      </div>
      <div
        className="smrttasks-sortable"
        onClick={() => handleSort(tab, "assignedTo")}
      >
        Assigned To{sortIndicator(tab, "assignedTo")}
      </div>
      <div
        className="smrttasks-sortable"
        onClick={() => handleSort(tab, "completed")}
      >
        Completed{sortIndicator(tab, "completed")}
      </div>
      <div
        className="smrttasks-sortable"
        onClick={() => handleSort(tab, "dateStarted")}
      >
        Date Started{sortIndicator(tab, "dateStarted")}
      </div>
      <div
        className="smrttasks-sortable"
        onClick={() => handleSort(tab, "dateCompleted")}
      >
        Date Completed{sortIndicator(tab, "dateCompleted")}
      </div>
      <div className="smrttasks-task-header-actions">Actions</div>
    </div>
  );

  return (
    <div className="smrttasks-page">
      <h1 className="smrttasks-title">Manage SMRT Tasks</h1>

      <div className="smrttasks-tabs">
        <div
          className={`smrttasks-tab${activeTab === "overview" ? " smrttasks-tab-active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </div>
        <div
          className={`smrttasks-tab${activeTab === "open" ? " smrttasks-tab-active" : ""}`}
          onClick={() => setActiveTab("open")}
        >
          <span className="smrttasks-tab-dot">🔴</span> Open
        </div>
        <div
          className={`smrttasks-tab${activeTab === "rejected" ? " smrttasks-tab-active" : ""}`}
          onClick={() => setActiveTab("rejected")}
        >
          <span className="smrttasks-tab-dot">🟡</span> Rejected
        </div>
        <div
          className={`smrttasks-tab${activeTab === "complete" ? " smrttasks-tab-active" : ""}`}
          onClick={() => setActiveTab("complete")}
        >
          <span className="smrttasks-tab-dot">🟢</span> Complete
        </div>
      </div>

      {activeTab === "open" && (
        <div className="smrttasks-card">
          {tasksLoading && <div className="smrttasks-empty">Loading...</div>}
          {!tasksLoading && tasksError && (
            <div className="smrttasks-empty">{tasksError}</div>
          )}
          {!tasksLoading && !tasksError && adminTasks.open.length === 0 && (
            <div className="smrttasks-empty">No open tasks.</div>
          )}
          {!tasksLoading && !tasksError && adminTasks.open.length > 0 &&
            renderAdminTaskHeader("open")}
          {!tasksLoading &&
            !tasksError &&
            sortedTasks("open", adminTasks.open).map((t) =>
              renderAdminTaskRow(t, "open"),
            )}
        </div>
      )}

      {activeTab === "rejected" && (
        <div className="smrttasks-card">
          {tasksLoading && <div className="smrttasks-empty">Loading...</div>}
          {!tasksLoading && tasksError && (
            <div className="smrttasks-empty">{tasksError}</div>
          )}
          {!tasksLoading && !tasksError && adminTasks.rejected.length === 0 && (
            <div className="smrttasks-empty">No rejected tasks.</div>
          )}
          {!tasksLoading && !tasksError && adminTasks.rejected.length > 0 &&
            renderAdminTaskHeader("rejected")}
          {!tasksLoading &&
            !tasksError &&
            sortedTasks("rejected", adminTasks.rejected).map((t) =>
              renderAdminTaskRow(t, "rejected"),
            )}
        </div>
      )}

      {activeTab === "complete" && (
        <div className="smrttasks-card">
          {!tasksLoading && !tasksError && adminTasks.complete.length > 0 && (
            <div className="smrttasks-toolbar">
              <button
                type="button"
                className="smrttasks-btn-primary"
                disabled={
                  adminTasks.complete.filter((t) => selectedIds.has(t.id))
                    .length === 0
                }
                onClick={handleBulkDelete}
              >
                Delete Selected
              </button>
            </div>
          )}
          {tasksLoading && <div className="smrttasks-empty">Loading...</div>}
          {!tasksLoading && tasksError && (
            <div className="smrttasks-empty">{tasksError}</div>
          )}
          {!tasksLoading && !tasksError && adminTasks.complete.length === 0 && (
            <div className="smrttasks-empty">No completed tasks.</div>
          )}
          {!tasksLoading && !tasksError && adminTasks.complete.length > 0 &&
            renderAdminTaskHeader("complete")}
          {!tasksLoading &&
            !tasksError &&
            sortedTasks("complete", adminTasks.complete).map((t) =>
              renderAdminTaskRow(t, "complete"),
            )}
        </div>
      )}

      {activeTab === "overview" && (
        <div className="smrttasks-card">
          <div className="smrttasks-toolbar">
            <button
              type="button"
              className="smrttasks-btn-primary"
              onClick={openCreate}
            >
              + Add Task Type
            </button>
          </div>

          {loading && <div className="smrttasks-empty">Loading...</div>}

          {!loading && loadError && (
            <div className="smrttasks-empty">{loadError}</div>
          )}

          {!loading && !loadError && taskTypes.length === 0 && (
            <div className="smrttasks-empty">
              No task types yet. Click "+ Add Task Type" to create one.
            </div>
          )}

          {!loading && !loadError && taskTypes.length > 0 && (
            <div className="smrttasks-overview-header">
              <div>Task Type</div>
              <div>Task Count</div>
              <div className="smrttasks-overview-header-actions">Actions</div>
            </div>
          )}

          {!loading &&
            !loadError &&
            taskTypes.map((tt, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === taskTypes.length - 1;
              return (
                <div key={tt.id} className="smrttasks-row">
                  <div className="smrttasks-row-name">{tt.name}</div>

                  <div className="smrttasks-counts">
                    <span
                      className="smrttasks-count smrttasks-count-open"
                      title="Open tasks"
                    >
                      <span className="smrttasks-count-dot">🔴</span>
                      <span className="smrttasks-count-value">
                        {tt.counts.open}
                      </span>
                    </span>
                    <span
                      className="smrttasks-count smrttasks-count-rejected"
                      title="Rejected tasks"
                    >
                      <span className="smrttasks-count-dot">🟡</span>
                      <span className="smrttasks-count-value">
                        {tt.counts.rejected}
                      </span>
                    </span>
                    <span
                      className="smrttasks-count smrttasks-count-complete"
                      title="Complete tasks"
                    >
                      <span className="smrttasks-count-dot">🟢</span>
                      <span className="smrttasks-count-value">
                        {tt.counts.complete}
                      </span>
                    </span>
                  </div>

                  <div className="smrttasks-actions">
                    <button
                      type="button"
                      className="smrttasks-btn-arrow"
                      title="Move up"
                      disabled={isFirst}
                      onClick={() => handleMoveUp(tt.id)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="smrttasks-btn-arrow"
                      title="Move down"
                      disabled={isLast}
                      onClick={() => handleMoveDown(tt.id)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="smrttasks-btn-icon smrttasks-btn-edit"
                      title="Edit"
                      onClick={() => openEdit(tt)}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="smrttasks-btn-icon smrttasks-btn-delete"
                      title="Delete"
                      onClick={() => handleDelete(tt)}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <SMRTTasksAddTypeModal
        isOpen={modalOpen}
        mode={modalMode}
        initialName={editingInitialName}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
}
